import React, { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Slider,
  Stack,
  SxProps,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import { Checkbox } from "@ui/index";

import { useIsDesktop } from "@hooks/layout";

import { useFormikContext } from "formik";
import get from "lodash.get";
import isNill from "lodash/isNil";

const MAX_VIEWS = 1_000_001;

interface IFormikTextInputProps {
  sx?: SxProps;
  fieldType?: EFormikFieldType;
  fieldName: string;
  label: string;
  /** Render `label` as a small persistent label ABOVE the box (so long field
      names aren't truncated as a placeholder and the context stays visible
      after the field is filled). Opt-in — other forms keep placeholder-only. */
  labelAbove?: boolean;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  multiLine?: boolean;
  rows?: number;
  autoComplete?: string;
  /**
   * Rewrite what the customer typed before it is stored, on every keystroke.
   *
   * Used for the signup name, where "PATRICK" should become "Patrick" and
   * "anne-marie" should become "Anne-Marie" as they type, rather than being
   * silently corrected after submitting or — worse — rejected with an error
   * telling somebody their own name is wrong.
   *
   * Safe on every keystroke ONLY because the transforms used here never change
   * the length of the value; the caret would otherwise jump to the end mid-word.
   * A transformer that inserts or removes characters wants to run on blur.
   */
  transformValue?: (value: string) => string;
  isSliderWithTextFields?: boolean;
  maxSlider?: number;
  minSlider?: number;
  maxSliderPlaceholder?: string;
  step?: number;
  InputAdornment?: ReactNode;
  checkIsValid?: boolean;
  inputRef?: (ref: any) => void;
  isOneMillionPlusFormat?: boolean;
  isBudget?: boolean;
  /** Suppress the "This field is required." helper text while the field is EMPTY
      (the red border + any shake still convey it). Format errors on a non-empty
      value still show. Opt-in so other forms keep their required messages. */
  hideRequiredMessage?: boolean;
  /** Suppress the helper text for THIS field entirely while keeping the error
      state itself — red border, shake, and the blocked submit all still apply.
      For password fields, where the live requirements checklist below the input
      already says exactly what is wrong; a second sentence repeating it is
      noise. Opt-in, so every other field keeps its message. */
  hideErrorText?: boolean;
  /** Force the error styling (red border) on regardless of Formik's own state.
      Needed where a rule cannot come from validation: these forms run with
      validateOnChange/Blur off, so `errors` is empty until submit, and submit
      is blocked by a disabled button — meaning a live rule like "passwords must
      match" can never light the field through the normal path. */
  forceError?: boolean;
}

export enum EFormikFieldType {
  Text = "Text",
  CheckBox = "CheckBox",
  // Enum values intentionally mirror MUI component names for clarity at
  // call sites (`EFormikFieldType.ToggleButton`); the shadowing of the
  // outer MUI imports is harmless here since these are enum members not
  // standalone variables.
  // eslint-disable-next-line @typescript-eslint/no-shadow
  ToggleButton = "ToggleButton",
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Slider = "Slider",
}

const FormikInput: FC<IFormikTextInputProps> = ({
  fieldName,
  label,
  labelAbove = false,
  disabled = false,
  placeholder = "",
  type = "text",
  fieldType = EFormikFieldType.Text,
  multiLine = false,
  rows,
  autoComplete = "on",
  transformValue,
  isSliderWithTextFields = false,
  maxSlider = 0,
  minSlider = 0,
  maxSliderPlaceholder = "",
  step = 1,
  InputAdornment,
  checkIsValid = false,
  inputRef,
  isOneMillionPlusFormat = false,
  isBudget = false,
  hideRequiredMessage = false,
  hideErrorText = false,
  forceError = false,
  sx,
}) => {
  const isDesktop = useIsDesktop();
  const { values, errors, handleChange, handleBlur, touched, setFieldValue } = useFormikContext<any>();
  const hasError = (!!errors[fieldName] && !!touched[fieldName]) || forceError;
  const handleSetField = (field: string, value: number | number[]) => {
    setFieldValue(field, value);
  };

  const formatSliderTextField = (value: number | string, oneMillionPlus: boolean, budgetFmt: boolean) => {
    if (isNill(value)) {
      return value;
    }

    if (oneMillionPlus) {
      return value >= MAX_VIEWS ? "1M+" : value;
    }

    return budgetFmt ? `${value} USD` : value;
  };

  const { t } = useTranslation("formikInput");

  const checkIsValidCondition = get(values, fieldName) ? InputAdornment : null;

  return (
    <FormControl
      fullWidth={fieldType !== EFormikFieldType.CheckBox}
      hiddenLabel={!values[fieldName]}
      error={hasError}
    >
      {fieldType === EFormikFieldType.Text && labelAbove && (
        <label
          htmlFor={fieldName}
          style={{
            display: "block",
            marginBottom: "6px",
            textAlign: "left",
            fontFamily: "'Raleway', sans-serif",
            fontSize: "0.8125rem",
            fontWeight: 500,
            letterSpacing: "0.01em",
            color: "rgba(251, 244, 236, 0.78)",
          }}
        >
          {label}
        </label>
      )}
      {fieldType === EFormikFieldType.Text && (
        <TextField
          id={fieldName}
          inputRef={(ref) => inputRef && inputRef(ref)}
          disabled={disabled}
          fullWidth
          variant="outlined"
          name={fieldName}
          type={type}
          onChange={
            transformValue
              ? (e) => {
                  // Rebuilt rather than mutated: Formik reads `name` off the
                  // target, and handing it a synthetic object without one
                  // silently updates nothing.
                  e.target.value = transformValue(e.target.value);
                  handleChange(e);
                }
              : handleChange
          }
          onBlur={handleBlur}
          value={get(values, fieldName)}
          placeholder={placeholder}
          error={hasError}
          multiline={multiLine}
          rows={rows}
          autoComplete={autoComplete}
          InputProps={
            checkIsValid
              ? {
                  endAdornment: checkIsValidCondition,
                }
              : { endAdornment: InputAdornment }
          }
          sx={{
            textarea: {
              paddingLeft: "0 !important",
            },
            ".MuiInputBase-root": {
              paddingRight: InputAdornment ? "16px" : undefined,
            },
            ...sx,
          }}
        />
      )}

      {errors[fieldName] && touched[fieldName] && fieldType !== EFormikFieldType.Slider &&
        !hideErrorText &&
        !(hideRequiredMessage && !get(values, fieldName)) && (
        <FormHelperText id={fieldName}>{errors[fieldName] as string}</FormHelperText>
      )}

      {fieldType === EFormikFieldType.CheckBox && (
        <FormControlLabel
          control={
            <Checkbox name={fieldName} onChange={handleChange} onBlur={handleBlur} checked={values[fieldName]} />
          }
          disabled={disabled}
          label={label}
          labelPlacement="end"
          sx={sx}
        />
      )}

      {fieldType === EFormikFieldType.ToggleButton && (
        <>
          <FormLabel sx={{ textAlign: "start", marginBottom: "16px" }}>{label}</FormLabel>

          <ToggleButtonGroup
            value={get(values, fieldName)}
            onChange={(_, value) => handleSetField(fieldName, value)}
            exclusive
            id={fieldName}
            sx={{ flexWrap: "wrap" }}
          >
            <ToggleButton value="0-100">
              <Box bgcolor="rgba(69, 52, 37, 0.80)" padding="16px 8px" borderRadius="8px">
                0 - 100
              </Box>
            </ToggleButton>
            <ToggleButton value="100-1k">
              <Box bgcolor="rgba(69, 52, 37, 0.80)" padding="16px 8px" borderRadius="8px">
                100 - 1K
              </Box>
            </ToggleButton>
            <ToggleButton value="1k-10k">
              <Box bgcolor="rgba(69, 52, 37, 0.80)" padding="16px 8px" borderRadius="8px">
                1K - 10K
              </Box>
            </ToggleButton>
            <ToggleButton value="10k-100k">
              <Box bgcolor="rgba(69, 52, 37, 0.80)" padding="16px 8px" borderRadius="8px">
                10K - 100K
              </Box>
            </ToggleButton>
            <ToggleButton value="100k-500k">
              <Box bgcolor="rgba(69, 52, 37, 0.80)" padding="16px 8px" borderRadius="8px">
                100K - 500K
              </Box>
            </ToggleButton>
            <ToggleButton value="500k-1m">
              <Box bgcolor="rgba(69, 52, 37, 0.80)" padding="16px 8px" borderRadius="8px">
                500K - 1M
              </Box>
            </ToggleButton>
            <ToggleButton value=">1m">
              <Box bgcolor="rgba(69, 52, 37, 0.80)" padding="16px 8px" borderRadius="8px">
                {">"} 1M
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </>
      )}

      {fieldType === EFormikFieldType.Slider && (
        <>
          <FormLabel sx={{ textAlign: "start", marginBottom: "16px" }}>{label}</FormLabel>

          <Slider
            name={fieldName}
            min={minSlider}
            max={maxSlider}
            step={step}
            value={get(values, fieldName)?.every((el: number) => el === null) ? [0, maxSlider] : get(values, fieldName)}
            onChange={(_, value) => handleSetField(fieldName, value)}
          />

          {isSliderWithTextFields && isDesktop && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" justifyContent="center" alignItems="center" spacing="16px">
                <FormLabel sx={{ textAlign: "start" }}>{t("from")}</FormLabel>

                <TextField
                  disabled={disabled}
                  variant="outlined"
                  name={fieldName}
                  type="text"
                  onChange={(event) =>
                    !isNaN(Number(event.target.value)) &&
                    handleSetField(fieldName, [Number(event.target.value), get(values, fieldName)[1]])
                  }
                  onBlur={handleBlur}
                  value={formatSliderTextField(get(values, fieldName)[0], isOneMillionPlusFormat, isBudget)}
                  placeholder={isBudget ? `${t("notApplicable")} USD` : `${t("notApplicable")}`}
                  error={hasError}
                  autoComplete={autoComplete}
                />
              </Stack>

              <Stack direction="row" justifyContent="center" alignItems="center" spacing="16px">
                <FormLabel sx={{ textAlign: "start" }}>{t("to")}</FormLabel>

                <TextField
                  disabled={disabled}
                  variant="outlined"
                  name={fieldName}
                  type="text"
                  onChange={(event) =>
                    !isNaN(Number(event.target.value)) &&
                    handleSetField(fieldName, [get(values, fieldName)[0], Number(event.target.value)])
                  }
                  onBlur={handleBlur}
                  value={formatSliderTextField(get(values, fieldName)[1], isOneMillionPlusFormat, isBudget)}
                  placeholder={maxSliderPlaceholder}
                  error={hasError}
                  multiline={multiLine}
                  rows={rows}
                  autoComplete={autoComplete}
                />
              </Stack>
            </Stack>
          )}

          {isSliderWithTextFields && !isDesktop && (
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
              <TextField
                disabled={disabled}
                variant="outlined"
                name={fieldName}
                type="number"
                onChange={handleChange}
                onBlur={handleBlur}
                value={get(values, fieldName)[0]}
                placeholder={t("notApplicable") ?? ""}
                error={hasError}
                autoComplete={autoComplete}
                sx={{ width: "40%" }}
              />

              <Box width="20%">
                <FormLabel sx={{ textAlign: "start" }}>-</FormLabel>
              </Box>

              <TextField
                disabled={disabled}
                variant="outlined"
                name={fieldName}
                type="number"
                onChange={handleChange}
                onBlur={handleBlur}
                value={get(values, fieldName)[1]}
                placeholder={maxSliderPlaceholder}
                error={hasError}
                multiline={multiLine}
                rows={rows}
                autoComplete={autoComplete}
                sx={{ width: "40%" }}
              />
            </Stack>
          )}
        </>
      )}
    </FormControl>
  );
};

export default FormikInput;
